/**
 * Admin roster of the clinic's capture nodes (WEB-3252): one row per
 * Mike/Cameron device Zuul has adopted, with its room, instance id, live
 * status, and when it was last seen. Backs the "Capture nodes" section of the
 * admin Capture tab, alongside the per-room timeline and flagged appointments.
 */
import { useSessionStore } from "@auth/sessionStore";
import type { CaptureNode } from "@capture/captureClient";
import { statusForNode } from "@capture/captureNodeStatus";
import { useCaptureNodes } from "@capture/captureQueries";
import {
  EmptyNote,
  LabelTape,
  LedgerBody,
  LedgerCell,
  LedgerHead,
  LedgerHeadCell,
  LedgerRow,
  LedgerTable,
  Swipe,
  type SwipeTone,
} from "@scribenote/brian-design-system";
import { formatDateTime } from "@utils/format-date";
import { relativeTime } from "@utils/relative-time";

/**
 * The word each status says, and the highlighter it wears. Recording and Online
 * are both the device working as intended, so both take the working marker;
 * Unknown takes the flag, because a device we cannot hear from is the one to go
 * look at. Offline goes unmarked: a machine somebody switched off is a fact, not
 * an alarm, so it reads as plain annotation.
 */
const STATUS_WORD: Record<
  ReturnType<typeof statusForNode>,
  { label: string; tone: SwipeTone | null }
> = {
  recording: { label: "Recording", tone: "working" },
  online: { label: "Online", tone: "working" },
  offline: { label: "Offline", tone: null },
  stale: { label: "Unknown - link down", tone: "flag" },
};

const NodeRow = ({ node }: { node: CaptureNode }) => {
  const { label, tone } = STATUS_WORD[statusForNode(node)];
  const lastSeenMs = new Date(node.lastSeen).getTime();
  return (
    <LedgerRow data-testid={`capture-node-row-${node.uuid}`}>
      <LedgerCell>
        {node.roomName ? (
          // Someone walked over and stuck a label on the room. Room names are
          // free text, so the tape truncates rather than widening the sheet.
          <LabelTape className="max-w-[12rem] truncate align-middle">
            {node.roomName}
          </LabelTape>
        ) : (
          <span className="text-annotation">Unassigned</span>
        )}
      </LedgerCell>
      <LedgerCell mono className="text-annotation">
        {node.instanceId}
      </LedgerCell>
      <LedgerCell className="text-sm">
        {tone ? (
          <Swipe tone={tone} data-testid={`capture-node-status-${node.uuid}`}>
            {label}
          </Swipe>
        ) : (
          <span
            data-testid={`capture-node-status-${node.uuid}`}
            className="text-annotation"
          >
            {label}
          </span>
        )}
      </LedgerCell>
      <LedgerCell
        mono
        className="whitespace-nowrap text-annotation"
        title={formatDateTime(node.lastSeen)}
      >
        {Number.isNaN(lastSeenMs) ? "-" : relativeTime(lastSeenMs)}
      </LedgerCell>
    </LedgerRow>
  );
};

export const CaptureNodesList = () => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const { data: nodes } = useCaptureNodes(sessionKey);

  if (nodes.length === 0)
    return (
      <EmptyNote>
        I haven&rsquo;t heard from any capture devices yet. One shows up here
        once it&rsquo;s set up in the clinic and starts reporting.
      </EmptyNote>
    );

  const sorted = [...nodes].sort(
    (a, b) =>
      a.roomName.localeCompare(b.roomName) ||
      a.instanceId.localeCompare(b.instanceId),
  );

  return (
    <LedgerTable>
      <LedgerHead>
        <tr>
          <LedgerHeadCell>Room</LedgerHeadCell>
          <LedgerHeadCell>Instance</LedgerHeadCell>
          <LedgerHeadCell>Status</LedgerHeadCell>
          <LedgerHeadCell>Last seen</LedgerHeadCell>
        </tr>
      </LedgerHead>
      <LedgerBody>
        {sorted.map((node) => (
          <NodeRow key={node.uuid} node={node} />
        ))}
      </LedgerBody>
    </LedgerTable>
  );
};
